/**
 * swiss-b2b.js
 * Leichtgewichtiges Verhalten für die B2B-Landingpage:
 * 1) Scroll-Reveal via IntersectionObserver (Singleton, kein Layout-Thrash)
 * 2) Premium-Navigation (Design-Runde 3, 2026-08-09): transparent über dem
 *    Hero → soft Backdrop-Blur beim Scrollen → soft Aus-/Einblenden je
 *    nach Scroll-Richtung. Einziger Header-Mechanismus (Dawns eigenes
 *    Sticky-Header-Custom-Element ist über enable_sticky_header:false im
 *    Theme-Setting deaktiviert, siehe config/settings_data.json + Kommentar
 *    in swiss-b2b.css Abschnitt "PREMIUM-NAVIGATION").
 *
 * Der mobile Sticky-WhatsApp-Button (initStickyWhatsapp) wurde am 2026-07-19
 * entfernt (Co-Founder-Feedback: WhatsApp ist kein öffentliches Eingangstor
 * mehr, siehe swiss-b2b-hero.liquid).
 *
 * 3) Hero-Slideshow (2026-08-23): Crossfade zwischen mehreren Box-Motiven im
 *    Hero-Bildbereich, nur aktiv wenn 2+ "slide"-Blocks gesetzt sind (siehe
 *    swiss-b2b-hero.liquid). Auto-Play ~4.5s, Pause bei Hover/Fokus/
 *    verstecktem Tab, komplett deaktiviert bei prefers-reduced-motion.
 *
 * Kein Framework-Dependency, läuft nach DOMContentLoaded.
 */
(function () {
  'use strict';

  function initScrollReveal() {
    var items = document.querySelectorAll('.sb-reveal');
    if (!items.length) return;

    var prefersReducedMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Sicherheitsnetz Teil 1: kein Observer-Support oder Nutzer will
    // reduzierte Bewegung -> gar nicht erst "armen" (CSS-Default von
    // .sb-reveal ist bereits sichtbar, siehe swiss-b2b.css).
    if (!('IntersectionObserver' in window) || prefersReducedMotion) {
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );

    items.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      var alreadyInView = rect.top < window.innerHeight && rect.bottom > 0;
      if (alreadyInView) {
        // Beim Init schon im sichtbaren Bereich (z.B. Anchor-Sprung, sehr
        // kurzer Viewport) -> nie verstecken, sonst bliebe es dauerhaft
        // unsichtbar (der Observer feuert nur beim ERSTEN Reinscrollen).
        el.classList.add('is-visible');
        return;
      }
      el.classList.add('sb-reveal--armed');
      observer.observe(el);
    });

    // Sicherheitsnetz Teil 2: falls der Observer aus irgendeinem Grund nie
    // feuert (gedrosselter Hintergrund-Tab, Editor-Iframe-Eigenheiten,
    // o.ä.), werden "armed", aber noch unsichtbare Elemente zwangsweise
    // eingeblendet. Bewusst grosszügiges Zeitfenster (8s statt z.B. 3-4s):
    // dieser Timer läuft ab initScrollReveal() (nahe DOMContentLoaded),
    // ein normaler Ladevorgang mit Trackern/Pixeln kann allein bis
    // "networkidle" schon 5s+ brauchen — ein zu kurzer Timer würde die
    // Reveal-Animation für jeden Besucher zunichtemachen, der nicht
    // innerhalb der ersten paar Sekunden scrollt. 8s ist immer noch weit
    // vor "für immer unsichtbar", aber lang genug, um normale Ladezeiten
    // nicht mit einem echten Init-Fehler zu verwechseln.
    window.setTimeout(function () {
      document.querySelectorAll('.sb-reveal.sb-reveal--armed:not(.is-visible)').forEach(
        function (el) {
          el.classList.add('is-visible');
        }
      );
    }, 8000);
  }

  function initPremiumHeader() {
    var headerSection = document.getElementById('shopify-section-header');
    if (!headerSection) return;

    var wrapper = headerSection.querySelector('.header-wrapper');
    if (!wrapper) return;

    // Ab hier: soft in den Backdrop-Blur-Zustand übergehen (kurz nachdem
    // der Hero-Rand verlassen wird, nicht sofort bei Pixel 1 — vermeidet
    // Flackern bei winzigen Scroll-Zuckern ganz oben).
    var SCROLLED_AT = 48;
    // Erst AB hier darf der Header beim Runterscrollen überhaupt
    // verschwinden — verhindert, dass er schon innerhalb des Heros
    // wegfadet, bevor der Nutzer wirklich weiter zum Content scrollt.
    var HIDE_AT = 360;
    // Kleine Hysterese gegen Sub-Pixel-Jitter (kein Debounce/Timer nötig).
    var DIRECTION_DELTA = 4;

    var lastScrollY = window.pageYOffset || window.scrollY || 0;
    var ticking = false;

    function update() {
      var y = window.pageYOffset || window.scrollY || 0;
      var delta = y - lastScrollY;

      wrapper.classList.toggle('sb-header--scrolled', y > SCROLLED_AT);

      if (y <= HIDE_AT) {
        wrapper.classList.remove('sb-header--hidden');
      } else if (delta > DIRECTION_DELTA) {
        wrapper.classList.add('sb-header--hidden');
      } else if (delta < -DIRECTION_DELTA) {
        wrapper.classList.remove('sb-header--hidden');
      }

      lastScrollY = y;
      ticking = false;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    // Initialzustand direkt setzen (z.B. Seiten-Reload weiter unten im
    // Scroll, Anchor-Navigation von einer anderen Seite).
    update();

    window.addEventListener('scroll', onScroll, { passive: true });

    // Sicherheitsnetz: requestAnimationFrame wird in Hintergrund-/
    // verdeckten Tabs von Chromium gedrosselt oder komplett pausiert —
    // ein Scroll, der während dieser Zeit passiert, würde sonst nie
    // verarbeitet. Sobald der Tab wieder sichtbar wird, Zustand direkt
    // neu berechnen statt auf den nächsten Scroll zu warten.
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') {
        update();
      }
    });
  }

  // Hero-Slideshow (Kunden-Feedback "Seite lebendiger machen", 2026-08-23):
  // reiner Crossfade zwischen den .sb-hero__slide-Bildern, Dots klickbar,
  // Auto-Play pausiert bei Hover/Fokus/verstecktem Tab und respektiert
  // prefers-reduced-motion (dann bleibt nur das erste Bild stehen, kein
  // Timer). Läuft unabhängig pro Section-Instanz (mehrere Slideshows auf
  // einer Seite wären möglich, auch wenn aktuell nur der Hero eine hat).
  function initHeroSlideshow() {
    var mediaEls = document.querySelectorAll('.sb-hero__media--slideshow');
    if (!mediaEls.length) return;

    var prefersReducedMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var AUTOPLAY_MS = 4500;

    mediaEls.forEach(function (media) {
      var slides = media.querySelectorAll('.sb-hero__slide');
      var dots = media.querySelectorAll('.sb-hero__dot');
      if (slides.length < 2) return;

      var current = 0;
      var timer = null;
      var paused = false;

      function show(index) {
        slides[current].classList.remove('is-active');
        dots[current] && dots[current].classList.remove('is-active');
        dots[current] && dots[current].setAttribute('aria-selected', 'false');

        current = (index + slides.length) % slides.length;

        slides[current].classList.add('is-active');
        dots[current] && dots[current].classList.add('is-active');
        dots[current] && dots[current].setAttribute('aria-selected', 'true');
      }

      function next() {
        show(current + 1);
      }

      function start() {
        if (prefersReducedMotion || paused || timer) return;
        timer = window.setInterval(next, AUTOPLAY_MS);
      }

      function stop() {
        if (timer) {
          window.clearInterval(timer);
          timer = null;
        }
      }

      dots.forEach(function (dot, index) {
        dot.addEventListener('click', function () {
          show(index);
          // Nach manueller Auswahl den Auto-Play-Takt neu beginnen, statt
          // mitten im alten Intervall sofort wieder weiterzuspringen.
          stop();
          start();
        });
      });

      media.addEventListener('mouseenter', function () {
        paused = true;
        stop();
      });
      media.addEventListener('mouseleave', function () {
        paused = false;
        start();
      });
      media.addEventListener('focusin', function () {
        paused = true;
        stop();
      });
      media.addEventListener('focusout', function () {
        paused = false;
        start();
      });

      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible') {
          if (!paused) start();
        } else {
          stop();
        }
      });

      if (!prefersReducedMotion) {
        start();
      }
    });
  }

  function init() {
    initScrollReveal();
    initPremiumHeader();
    initHeroSlideshow();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
